/**
 *  @class
 *  @function CartSubscribeUpsell
 *
 *  Drives the cart drawer's subscribe & save card: a slide per one-time line
 *  that has a selling plan available, plus a one-click switch to that plan.
 *
 *  Registered as a custom element so it re-initialises itself every time the
 *  drawer is re-rendered by the Section Rendering API — the drawer's own
 *  re-bind pass (CartDrawer.removeProductEvent and friends) never sees it.
 */

if (!customElements.get('cart-subscribe-upsell')) {
  class CartSubscribeUpsell extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.track = this.querySelector('[data-upsell-track]');
      this.slides = Array.from(this.querySelectorAll('[data-upsell-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-upsell-dot]'));
      this.index = 0;

      if (!this.track || this.slides.length === 0) {
        return;
      }

      const prev = this.querySelector('[data-upsell-prev]');
      const next = this.querySelector('[data-upsell-next]');

      if (prev) prev.addEventListener('click', () => this.go(this.index - 1));
      if (next) next.addEventListener('click', () => this.go(this.index + 1));

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.go(parseInt(dot.dataset.index, 10)));
      });

      this.querySelectorAll('[data-upsell-switch]').forEach((button) => {
        button.addEventListener('click', () => this.switchToSubscription(button));
      });
    }

    go(index) {
      // Clamp rather than wrap, so the arrows visibly bottom out at the ends.
      this.index = Math.max(0, Math.min(index, this.slides.length - 1));
      this.track.style.transform = `translateX(-${this.index * 100}%)`;
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
    }

    getSectionsToRender() {
      return [
        { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
      ];
    }

    getSectionInnerHTML(html, selector) {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    switchToSubscription(button) {
      if (this.busy) {
        return;
      }
      this.busy = true;
      this.classList.add('is-loading');
      button.setAttribute('disabled', 'disabled');

      // change.js keys the line by its cart key here rather than its 1-based
      // index, so the request stays correct if the drawer's markup is stale.
      const body = JSON.stringify({
        id: button.dataset.lineKey,
        quantity: parseInt(button.dataset.quantity, 10),
        selling_plan: parseInt(button.dataset.sellingPlan, 10),
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      });

      fetch(`${theme.routes.cart_change_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body
      })
        .then((response) => response.text())
        .then((state) => {
          const parsedState = JSON.parse(state);

          if (parsedState.status || parsedState.errors) {
            // Leave the cart untouched and hand the line back to the customer
            // rather than silently doing nothing.
            this.busy = false;
            this.classList.remove('is-loading');
            button.removeAttribute('disabled');
            return;
          }

          this.getSectionsToRender().forEach((section) => {
            const container = document.getElementById(section.id);
            if (!container || !parsedState.sections) {
              return;
            }
            const elementToReplace = container.querySelector(section.selector) || container;
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });

          // Matches how the theme itself recovers the drawer's listeners after
          // a cart mutation (see AddToCart.renderContents in app.js).
          if (typeof CartDrawer !== 'undefined') {
            new CartDrawer();
          }

          dispatchCustomEvent('line-item:change:end', {
            quantity: button.dataset.quantity,
            cart: parsedState
          });
        })
        .catch(() => {
          this.busy = false;
          this.classList.remove('is-loading');
          button.removeAttribute('disabled');
        });
    }
  }

  customElements.define('cart-subscribe-upsell', CartSubscribeUpsell);
}
